import { formatDateToMDY } from '../../../constants/utils';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useCreatePromotionHook } from '../hooks/useCreatePromotionHook';
import { Grid, Card, Snackbar } from '@mui/material';
import DiscountOffer from './DiscountOffer';
import PromotionPreview from './PromotionPreview';
import FinancialOffer from './FinancialOffer';
import OfferSweetners from './OfferSweetners';
import { OR } from '../constants';
import Alert from '../../../components/Alert';
import Notes from './Notes';
import styles from './styles';
import {
  LoadingShimmer,
  BackButton,
  PromotionDatesSection,
  BranchesSection,
  FinanceOfferButton,
  PreviewHeader,
} from './CreatePromotionHelpers';

const CreatePromotions = () => {
  const { userStores, isAdmin } = useSelector((state) => state.auth);
  const storeOptions = useMemo(() => {
    const filteredStores = userStores.filter((store) => store.branch_name !== 'GALE Markets');
    const stores = filteredStores.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    return stores.sort((a, b) => a.label.localeCompare(b.label));
  }, [userStores]);
  const params = useParams();
  const navigate = useNavigate();
  const { id } = params;
  const [canShowFlipButton, setCanShowFlipButton] = useState(false);
  const [status, setStatus] = useState('Open');
  const mode = id ? 'Edit' : 'Create';
  const [flipOffer, setFlipOffer] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [isDiscountClickable, setIsDiscountClickable] = useState(false);
  const [isFinancialClickable, setIsFinancialClickable] = useState(false);
  const [isNotesClickable, setIsNotesClickable] = useState(false);
  const [selectedConjunction, setSelectedConjunction] = useState(OR);
  const [disableUPDATEBtn, setDisableUPDATEBtn] = useState(true);
  const [disableDRAFTBtn, setDisableDRAFTBtn] = useState(true);
  const { addDays } = require('date-fns');
  const currentDate = new Date();
  const [defaultStartDate, setDefaultStartDate] = useState(addDays(currentDate, 5));
  const [defaultEndDate, setDefaultEndDate] = useState(addDays(currentDate, 10));
  const [selectedStartDate, setSelectedStartDate] = useState(formatDateToMDY(defaultStartDate));
  const [selectedEndDate, setSelectedEndDate] = useState(formatDateToMDY(defaultEndDate));
  const [historicPromotionData, setHistoricPromotionData] = useState(null);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackBarSeverity, setSnackBarSeverity] = useState('success');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const {
    promotionData,
    setPromotionData,
    productsList,
    discountOffersList,
    financeOffersList,
    sweetnerOffersList,
    handleCreatePromotion,
    handleSavePromotion,
    handlePublishPromotion,
    handleDraftPromotion,
    previewData,
    loadedPromotionData,
    showBtnLoader,
    dataLoader,
    fetchHeadlineData,
    handleNotifyEmail,
    notifyEmail,
    showDraftBtnLoader,
    showUpdateBtnLoader,
    headlineLoading,
  } = useCreatePromotionHook({
    id,
    setStatus,
    setIsDiscountClickable,
    setIsFinancialClickable,
    setSelectedStores,
    setSelectedConjunction,
    setCanShowFlipButton,
    setFlipOffer,
    setDisableDRAFTBtn,
    setDisableUPDATEBtn,
    mode,
    setSelectedStartDate,
    setSelectedEndDate,
    historicPromotionData,
    setHistoricPromotionData,
    setShowSnackBar,
    setSnackBarSeverity,
    setSnackBarMessage,
    setIsNotesClickable,
  });
  const [minEndDate, setMinEndDate] = useState(defaultStartDate);

  const duplicatePromotionEnable = useMemo(
    () => mode === 'Edit' && promotionData?.is_duplicate && promotionData?.status === 'OPEN',
    [mode, promotionData?.is_duplicate, promotionData?.status]
  );

  const disableDateSelection = useMemo(() => {
    if (duplicatePromotionEnable) {
      return false;
    }
    const isDefaultOrSpecial =
      promotionData?.promotion_type === 'DEFAULT' || promotionData?.promotion_type === 'SPECIAL';
    return isAdmin && isDefaultOrSpecial && mode !== 'Create';
  }, [duplicatePromotionEnable, isAdmin, promotionData?.promotion_type, mode]);

  const isDateEditable = useMemo(
    () => promotionData?.status !== 'FROZEN' && promotionData?.status !== 'ARCHIVED',
    [promotionData?.status]
  );

  const shouldShowStoreDropdown = useMemo(
    () => (mode !== 'Create' && promotionData?.store_id) || mode === 'Create',
    [mode, promotionData?.store_id]
  );

  const isCardEnabled = useMemo(() => {
    return isAdmin || isDateEditable || !duplicatePromotionEnable;
  }, [isAdmin, isDateEditable, duplicatePromotionEnable]);

  useEffect(() => {
    if (mode === 'Create') {
      setPromotionData({
        start_date: formatDateToMDY(defaultStartDate),
        freeze_date: formatDateToMDY(defaultStartDate),
        end_date: formatDateToMDY(defaultEndDate),
      });
    }
  }, []);

  useEffect(() => {
    if (
      loadedPromotionData &&
      historicPromotionData !== null &&
      Object.keys(historicPromotionData).length !== 0
    ) {
      setDefaultStartDate(formatDateToMDY(new Date(historicPromotionData.start_date)));
      setDefaultEndDate(formatDateToMDY(new Date(historicPromotionData.end_date)));
      setMinEndDate(formatDateToMDY(new Date(historicPromotionData.start_date)));
      setPromotionData(JSON.parse(JSON.stringify(historicPromotionData)));
    }
  }, [loadedPromotionData, historicPromotionData]);

  function handleAddFinanceOffer() {
    let newFinanceOffer = {};
    let financeOffers = [...promotionData.finance_offer];
    if (promotionData.finance_offer.length === 2) {
      financeOffers.pop();
    } else {
      financeOffers.splice(1, 0, newFinanceOffer);
    }
    setPromotionData({
      ...promotionData,
      finance_offer: financeOffers,
    });
  }

  const handleFlipButtonClick = () => {
    setPromotionData({ ...promotionData, flip: !flipOffer });
    setFlipOffer(!flipOffer);
  };

  const handleStartDateChange = (date) => {
    const formattedDate = formatDateToMDY(date);
    setDefaultStartDate(formattedDate);
    setMinEndDate(formattedDate);
    setSelectedStartDate(formattedDate);
    setPromotionData({
      ...promotionData,
      start_date: formattedDate,
      freeze_date: formattedDate,
    });
  };

  const handleEndDateChange = (date) => {
    const formattedDate = formatDateToMDY(date);
    setDefaultEndDate(formattedDate);
    setSelectedEndDate(formattedDate);
    setPromotionData({ ...promotionData, end_date: formattedDate });
  };

  // Helper to update clickable states
  const updateClickableStates = (hasStores) => {
    setIsDiscountClickable(hasStores);
    setIsFinancialClickable(hasStores);
    setIsNotesClickable(hasStores);
  };

  const handleStoreChange = (value) => {
    const filteredValue = value.filter((v) => v !== null && v !== undefined);
    const hasStores = filteredValue?.length > 0;

    updateClickableStates(hasStores);

    if (hasStores) {
      setPromotionData({ ...promotionData, store_ids: filteredValue });
    }

    setSelectedStores(filteredValue);
  };
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSnackBar(false);
  };

  const showLoadingState = dataLoader && (!loadedPromotionData || mode === 'Create');
  const showMainContent = !dataLoader && (loadedPromotionData || mode === 'Create');

  return (
    <>
      {showLoadingState && <LoadingShimmer />}
      <BackButton navigate={navigate} />
      {showMainContent && (
        <Grid
          container
          spacing={4}
          sx={styles.gridContainer}
          style={{ height: '87vh', overflowY: 'scroll' }}
        >
          <Grid item xs={6} style={{ maxHeight: '100%', overflowY: 'auto', padding: '0 32px' }}>
            <PromotionDatesSection
              promotionData={promotionData}
              isCardEnabled={isCardEnabled}
              isAdmin={isAdmin}
              disableDateSelection={disableDateSelection}
              isDateEditable={isDateEditable}
              currentDate={currentDate}
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              handleStartDateChange={handleStartDateChange}
              handleEndDateChange={handleEndDateChange}
              minEndDate={minEndDate}
            />
            <BranchesSection
              promotionData={promotionData}
              shouldShowStoreDropdown={shouldShowStoreDropdown}
              storeOptions={storeOptions}
              selectedStores={selectedStores}
              handleStoreChange={handleStoreChange}
              mode={mode}
              headlineLoading={headlineLoading}
            />
            <DiscountOffer
              offersList={discountOffersList}
              defaultExpiryDate={defaultEndDate}
              promotionData={promotionData}
              setPromotionData={setPromotionData}
              mode={mode}
              isDiscountClickable={isDiscountClickable}
              isAdmin={isAdmin}
              productsList={productsList}
              setDisableDRAFTBtn={setDisableDRAFTBtn}
              setDisableUPDATEBtn={setDisableUPDATEBtn}
              loadedPromotionData={loadedPromotionData}
              isDateSelectionDisabled={headlineLoading}
              fetchHeadlineData={fetchHeadlineData}
            />
            <FinancialOffer
              index={0}
              offersList={financeOffersList}
              defaultExpiryDate={defaultEndDate}
              promotionData={promotionData}
              setPromotionData={setPromotionData}
              mode={mode}
              isFinancialClickable={isFinancialClickable}
              isAdmin={isAdmin}
              selectedConjunction={selectedConjunction}
              setSelectedConjunction={setSelectedConjunction}
              setDisableDRAFTBtn={setDisableDRAFTBtn}
              setDisableUPDATEBtn={setDisableUPDATEBtn}
              loadedPromotionData={loadedPromotionData}
              selectedStores={selectedStores}
              historicPromotionData={historicPromotionData}
              isDateSelectionDisabled={headlineLoading}
              fetchHeadlineData={fetchHeadlineData}
            />
            {promotionData?.finance_offer?.length === 2 && (
              <FinancialOffer
                index={1}
                offersList={financeOffersList}
                defaultExpiryDate={defaultEndDate}
                promotionData={promotionData}
                setPromotionData={setPromotionData}
                mode={mode}
                isFinancialClickable={isFinancialClickable}
                isAdmin={isAdmin}
                selectedConjunction={selectedConjunction}
                setSelectedConjunction={setSelectedConjunction}
                setDisableDRAFTBtn={setDisableDRAFTBtn}
                setDisableUPDATEBtn={setDisableUPDATEBtn}
                loadedPromotionData={loadedPromotionData}
                selectedStores={selectedStores}
                historicPromotionData={historicPromotionData}
                isDateSelectionDisabled={headlineLoading}
                fetchHeadlineData={fetchHeadlineData}
              />
            )}
            {promotionData?.finance_offer?.length >= 1 && (
              <FinanceOfferButton
                promotionData={promotionData}
                handleAddFinanceOffer={handleAddFinanceOffer}
              />
            )}

            <OfferSweetners
              offersList={sweetnerOffersList}
              defaultExpiryDate={defaultEndDate}
              promotionData={promotionData}
              setPromotionData={setPromotionData}
              mode={mode}
              isAdmin={isAdmin}
              loadedPromotionData={loadedPromotionData}
              isDateSelectionDisabled={headlineLoading}
            />

            <Notes
              promotionData={promotionData}
              mode={mode}
              isNotesClickable={isNotesClickable}
              setPromotionData={setPromotionData}
              fetchHeadlineData={fetchHeadlineData}
              loadedPromotionData={loadedPromotionData}
              historicPromotionData={historicPromotionData}
            />
          </Grid>
          <Grid
            item
            xs={6}
            sx={styles.offer}
            style={{ padding: '0 32px', height: '700px', overflowY: 'auto' }}
          >
            <PreviewHeader
              status={status}
              canShowFlipButton={canShowFlipButton}
              flipOffer={flipOffer}
              handleFlipButtonClick={handleFlipButtonClick}
            />
            <Card sx={{ ...styles.preview, ...styles.card }}>
              <PromotionPreview
                data={previewData}
                promotionData={promotionData}
                flipValue={flipOffer}
                handleCreatePromotion={handleCreatePromotion}
                handleSavePromotion={handleSavePromotion}
                handleDraftPromotion={handleDraftPromotion}
                handlePublishPromotion={handlePublishPromotion}
                mode={mode}
                isAdmin={isAdmin}
                showBtnLoader={showBtnLoader}
                promotionStatus={status}
                disableDRAFTBtn={disableDRAFTBtn}
                disableUPDATEBtn={disableUPDATEBtn}
                selectedStores={selectedStores}
                defaultEndDate={defaultEndDate}
                defaultStartDate={defaultStartDate}
                selectedConjunction={selectedConjunction}
                handleNotifyEmail={handleNotifyEmail}
                notifyEmail={notifyEmail}
                showDraftBtnLoader={showDraftBtnLoader}
                showUpdateBtnLoader={showUpdateBtnLoader}
              />
            </Card>
          </Grid>
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={showSnackBar}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <Alert severity={snackBarSeverity}>{snackBarMessage}</Alert>
          </Snackbar>
        </Grid>
      )}
    </>
  );
};
export default CreatePromotions;
